import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { ChevronDown, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import Badge from "@/components/ui/Badge";
import PlatformGlyph from "@/components/ui/PlatformGlyph";
import { APP_PLATFORM_LABEL, cn } from "@/lib/utils";
import { systemModeLabel } from "@/lib/systemMode";
import type { AppSystemMode } from "@/types";

/**
 * Header app-tree selector primitive.
 *
 * Source: docs/engineering/contracts/UI_COMPONENT_CONTRACT.md L22
 * (IA-REDESIGN 2026-05-07; ships under BRIEF_ia-redesign-frontend-implementation
 * § 4.2).
 *
 * Single-select-always invariant. Groups apps by platform (iOS / Android).
 * Footer `+ Add new app` action. Two-level depth only (Platform group → App).
 */

export type TreeSelectPlatform = "ios" | "android";

export interface AppTreeItem {
  id: string;
  name: string;
  platform: TreeSelectPlatform;
  // Operator-visible lifecycle mode, already resolved through
  // `effectiveMode(system_mode, operator_mode_override)` by the caller so
  // the tree row matches the Header badge for the same app.
  mode: AppSystemMode;
  runningCount: number;
  // ENG-RUNNING-COUNTS-STORE-FACT: the share of `runningCount` that is the
  // team's own console tests (ASOLoop did not start them) — printed with its
  // own noun beside the running badge.
  consoleCount: number;
  iconUrl?: string | null;
}

interface TreeSelectProps {
  apps: AppTreeItem[];
  selected: string | null;
  onSelect: (appId: string) => void;
  onAddNew: () => void;
  onTeamSpace: () => void;
  className?: string;
  disabled?: boolean;
}

export default function TreeSelect({
  apps,
  selected,
  onSelect,
  onAddNew,
  onTeamSpace,
  className,
  disabled,
}: TreeSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const selectedApp = apps.find((app) => app.id === selected) ?? null;
  const platforms: TreeSelectPlatform[] = ["ios", "android"];
  const grouped: Record<TreeSelectPlatform, AppTreeItem[]> = {
    ios: apps.filter((app) => app.platform === "ios"),
    android: apps.filter((app) => app.platform === "android"),
  };
  // Flat option order matches the rendered order (iOS group, then Android).
  const orderedApps = platforms.flatMap((platform) => grouped[platform]);

  // On open, move focus to the selected option (or the first) so keyboard
  // users land inside the listbox per the WAI-ARIA listbox pattern.
  useEffect(() => {
    if (!open) return;
    const selectedIdx = orderedApps.findIndex((app) => app.id === selected);
    const idx = selectedIdx >= 0 ? selectedIdx : 0;
    const handle = window.setTimeout(() => optionRefs.current[idx]?.focus(), 0);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function focusOption(index: number) {
    const count = orderedApps.length;
    if (count === 0) return;
    const next = ((index % count) + count) % count;
    optionRefs.current[next]?.focus();
  }

  // Roving focus across options: Arrow keys (with wrap) + Home/End. Enter/Space
  // are native <button> activation; Escape-to-close is handled globally above.
  function handleListboxKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (orderedApps.length === 0) return;
    const current = optionRefs.current.findIndex(
      (el) => el === document.activeElement,
    );
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(current < 0 ? 0 : current + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(current < 0 ? orderedApps.length - 1 : current - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(orderedApps.length - 1);
    }
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("ui.treeSelect.appSwitcherAriaLabel")}
        className={cn(
          "flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-900",
          "hover:border-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        {selectedApp ? (
          <>
            <AppIcon app={selectedApp} />
            <span className="max-w-[12rem] truncate">{selectedApp.name}</span>
            <span className="text-xs font-normal text-gray-500">
              {APP_PLATFORM_LABEL[selectedApp.platform]}
            </span>
          </>
        ) : (
          <span className="text-gray-500">{t("ui.treeSelect.selectApp")}</span>
        )}
        <ChevronDown size={16} className="text-gray-400" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={t("ui.treeSelect.appsListboxAriaLabel")}
          onKeyDown={handleListboxKeyDown}
          className={cn(
            "absolute left-0 top-full z-40 mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-lg",
          )}
        >
          <div className="max-h-80 overflow-y-auto py-1">
            {apps.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">
                {t("ui.treeSelect.noAppsYet")}
              </div>
            ) : (
              platforms.map((platform) => {
                const platformApps = grouped[platform];
                if (platformApps.length === 0) return null;
                return (
                  <div key={platform} className="py-1">
                    <div className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted">
                      <PlatformGlyph platform={platform} />
                      {APP_PLATFORM_LABEL[platform]}
                    </div>
                    {platformApps.map((app) => {
                      const isSelected = app.id === selected;
                      const flatIndex = orderedApps.findIndex(
                        (item) => item.id === app.id,
                      );
                      return (
                        <button
                          key={app.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          ref={(el) => {
                            optionRefs.current[flatIndex] = el;
                          }}
                          onClick={() => {
                            onSelect(app.id);
                            setOpen(false);
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                            "focus-visible:outline-none focus-visible:bg-gray-50",
                            isSelected
                              ? "bg-primary-50 text-primary-text"
                              : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          <AppIcon app={app} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {app.name}
                            </span>
                            {app.consoleCount > 0 && (
                              // ENG-RUNNING-COUNTS-STORE-FACT: the console
                              // share of the count on its own muted line.
                              // A second pill would overflow the w-80 row
                              // and clip the name (the R29-1 name budget).
                              <span className="block truncate text-xs font-normal text-gray-500">
                                {t("ui.treeSelect.consoleCount", {
                                  count: app.consoleCount,
                                })}
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-xs font-normal text-gray-500">
                            {systemModeLabel[app.mode]}
                          </span>
                          {app.runningCount > 0 && (
                            // C10 M3-R29-1 (48 Rule 9): the count prints
                            // its noun through the catalog on the Badge
                            // primitive -- no tooltip / title (a focusable
                            // nested in role=option), never a bare integer.
                            <Badge className="shrink-0 whitespace-nowrap">
                              {t("ui.treeSelect.runningCount", {
                                count: app.runningCount,
                              })}
                            </Badge>
                          )}
                          {isSelected && (
                            <span
                              aria-hidden="true"
                              className="ml-1 size-2 shrink-0 rounded-full bg-primary-500"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onAddNew();
            }}
            className={cn(
              "flex w-full items-center gap-2 border-t border-gray-200 px-3 py-2 text-left text-sm font-medium text-primary-text transition-colors",
              "hover:bg-primary-50 focus-visible:outline-none focus-visible:bg-primary-50",
            )}
          >
            <Plus size={16} aria-hidden="true" />
            <span>{t("ui.treeSelect.addNewApp")}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onTeamSpace();
            }}
            className={cn(
              "flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors",
              "hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-gray-50",
            )}
          >
            <Users size={16} aria-hidden="true" />
            <span>{t("ui.treeSelect.teamWorkspace")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function AppIcon({ app }: { app: AppTreeItem }) {
  if (app.iconUrl) {
    return (
      <img
        src={app.iconUrl}
        alt=""
        className="size-6 shrink-0 rounded"
        aria-hidden="true"
      />
    );
  }
  const monogram = app.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return (
    <span
      aria-hidden="true"
      className="flex size-6 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-600"
    >
      {monogram || "?"}
    </span>
  );
}
